import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppLinkNodes, ILinkDoc, ILinkNode } from "./AppLinks";
import { AppEditMark, AppLiveChange } from "./AppLiveEvents";
import { AppLiveRules } from "./AppLiveRules";
import { AppTranslate } from "./AppTranslate";
import { AppTranslateBubble, ITrDoc, ITrNode } from "./AppTranslateBubble";

// AppLiveEdit - AN EDITED MESSAGE, OR ONE DELETED FOR EVERYONE, CHANGES ON ITS BUBBLE WITHIN SECONDS (2026-10-01,
// owner-picked "Live reactions, edits, links"). `AppLiveEvents` holds the wire and the mark; this is the act.
//
// ⭐ WHAT THE REBUILT PAGE SHOWS IS WHAT THIS DRAWS (`DossierHtml.Bubble` over `ChangeBook.Apply`):
//   - an EDIT: the bubble's words become the new ones (with their links - `AppLinkNodes`), and the bubble carries
//     `data-ed="1"` so the stylesheet writes "edited" before the clock (`AppEditMark`);
//   - a REVOKE: the bubble says `[deleted]` - no photo, no voice note, no translation, no "edited".
// ⛔ ONLY THE AUTHOR'S OWN CHANGE: applied only to a bubble on the side the change came from
//    (`AppEditMark.SideOk`). A contact cannot rewrite what the owner said - whatever the wire carries.
// ⛔ NEWER WINS, A REVOKE IS FINAL: a change older than the one already applied to that message is ignored, and
//    nothing changes a message that was taken back.
// ⭐ TRANSLATION: the old translation goes (flag, hidden original, the `tr` class) - it was of the OLD words. The
//    change then says what to show: the new words in my language with the original behind the flag (`Needed`), or
//    the new words as written while their translation is made (`Pending` - the feed waits for the patch), or just
//    the new words.
// ⭐ ANY BUBBLE WITH A WHATSAPP ID: the page's own, a live one, the owner's just-sent one once it has its id.
// ⛔ TEXT BY TEXT NODES / `textContent` ONLY - never markup. ⛔ NO INLINE STYLE. ⛔ READ-ONLY (decision 9).
// ⚠ SHAPED BY INTERFACES, so the suite drives it with a small fake document.
// ⛔ HABITAT-FREE - IT MUST NOT IMPORT `AppChatAppend` (which imports `AppSource`: `window`, `XMLHttpRequest`). The
//    Bridge's contract test compiles this file with no DOM library (`LinkContract.test.ts` - the live bubble against
//    the page writer's), and tsx would run it happily while `npm run typecheck` failed. The two names it shares with
//    the appender (`data-mid`, `wac-state`) are constants here, pinned to the appender's by `AppLiveEvents.test.ts`.

/** The node members this touches - a real `Element` has them all (and so does `IChatNode`, for what they share). */
export interface IEditNode {
    textContent: string | null;
    className: string;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    removeAttribute(Name: string): void;
    insertBefore(Child: IEditNode, Ref: IEditNode | null): unknown;
    removeChild(Child: IEditNode): unknown;
    querySelector(Selector: string): IEditNode | null;
}

/** The document members this touches. */
export interface IEditDoc {
    querySelectorAll(Selector: string): ArrayLike<IEditNode>;
    createElement(Tag: string): IEditNode;
}

/** Bubble lookup needs only this attribute reader, independent of edit/feed node ports. */
export interface IAppBubbleNode {
    getAttribute(Name: string): string | null;
}

export interface IAppBubbleDoc<T extends IAppBubbleNode> {
    querySelectorAll(Selector: string): ArrayLike<T>;
}

/** What one answer's changes did. */
export class AppLiveEditDone {
    /** Bubbles changed. */
    public Changed: number = 0;
    /** Message ids whose new words are being translated - the feed waits for their patch. */
    public Pending: string[] = [];
    /** Message ids that stopped waiting (edited again without a pending translation, or taken back). */
    public Settled: string[] = [];
}

export class AppLiveEdit {
    /** The WhatsApp message id on every bubble - `AppChatAppend.MidAttr` (pinned by the suite). */
    public static readonly MidAttr: string = "data-mid";
    /** A sent bubble's status line - `AppChatAppend.StateClass` (pinned by the suite); new words go before it. */
    public static readonly StateClass: string = "wac-state";

    /** The time of the change last applied to each message. */
    private At: Record<string, number> = {};
    private Revoked: Record<string, boolean> = {};

    /** One answer's changes, oldest first. */
    public Apply = (Doc: IEditDoc, Changes: AppLiveChange[]): AppLiveEditDone => {
        const Out: AppLiveEditDone = new AppLiveEditDone();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            for (const One of Changes) {
                // ⭐ (owner 2026-10-22, "Deleted messages go away also from WAC") deleted on the subscriber's phone.
                if (One.HideAll === true) { Out.Changed = Out.Changed + AppLiveEdit.RemoveAll(Doc, Out); continue; }
                if (One.Hide === true) { Out.Changed = Out.Changed + AppLiveEdit.Remove(Doc, One.To, Out); continue; }
                const Bubble: IEditNode | null = AppLiveEdit.BubbleFor(Doc, One.To);
                if (Bubble === null || this.Allowed(Bubble as IEditNode, One) === false) { continue; }
                this.At[One.To] = Date.parse(One.At);
                Out.Changed = Out.Changed + 1;
                if (One.Revoke === true) {
                    this.Revoked[One.To] = true;
                    AppLiveEdit.Revoke(Doc, Bubble as IEditNode);
                    Out.Settled.push(One.To);
                    continue;
                }
                AppLiveEdit.Edit(Doc, Bubble as IEditNode, One);
                if (One.Pending === true) { Out.Pending.push(One.To); } else { Out.Settled.push(One.To); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.Apply");
            return Out;
        }
    };

    /** The bubble with this id leaves the page (1), or there was none (0). Its translation wait settles. */
    private static Remove = (Doc: IEditDoc, Mid: string, Out: AppLiveEditDone): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const Bubble: IEditNode | null = AppLiveEdit.BubbleFor(Doc, Mid);
            if (Bubble === null) { return 0; }
            AppLiveEdit.Detach(Bubble as IEditNode);
            Out.Settled.push(Mid);
            return 1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.Remove");
            return 0;
        }
    };

    /** Every bubble leaves the page (the chat was deleted on the phone). */
    private static RemoveAll = (Doc: IEditDoc, Out: AppLiveEditDone): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const All: ArrayLike<IEditNode> = Doc.querySelectorAll("[" + AppLiveEdit.MidAttr + "]");
            const List: IEditNode[] = [];
            for (let At: number = 0; At < All.length; At = At + 1) { List.push(All[At]); }
            for (const One of List) { Out.Settled.push(nz(One.getAttribute(AppLiveEdit.MidAttr))); AppLiveEdit.Detach(One); }
            return List.length;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.RemoveAll");
            return 0;
        }
    };

    /** A node out of the document: `remove` on a real element, else its parent's `removeChild`.
     *  DOCUMENTED EXCEPTION: two DOM calls; its callers hold the guard. ERROR-PROPAGATION-JUSTIFIED: a throw reaches them. */
    private static Detach = (Node: IEditNode): void => {
        const Self: { remove?: () => void; parentNode?: IEditNode | null } = Node as unknown as { remove?: () => void; parentNode?: IEditNode | null };
        if (typeof Self.remove === "function") { Self.remove(); return; }
        if (Self.parentNode !== undefined && Self.parentNode !== null) { (Self.parentNode as IEditNode).removeChild(Node); }
    };

    /** ⛔ The author's side, not taken back, and not older than what the bubble already shows. */
    private Allowed = (Bubble: IEditNode, One: AppLiveChange): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (AppEditMark.SideOk(Bubble.className, One.FromMe) === false) { return false; }
            if (this.Revoked[One.To] === true) { return false; }
            const Ms: number = Date.parse(One.At);
            if (isNaN(Ms) === true) { return false; }
            const Held: number | undefined = this.At[One.To];
            return Held === undefined || Ms >= Held;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.Allowed");
            return false;
        }
    };

    /** The bubble with this `data-mid`. ⛔ Compared in a loop - the id never enters a selector. */
    public static BubbleFor = <T extends IAppBubbleNode>(Doc: IAppBubbleDoc<T>, Mid: string): T | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const All: ArrayLike<T> = Doc.querySelectorAll("[" + AppLiveEdit.MidAttr + "]");
            for (let At: number = 0; At < All.length; At = At + 1) {
                if (All[At].getAttribute(AppLiveEdit.MidAttr) === Mid) { return All[At]; }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.BubbleFor");
            return null;
        }
    };

    /** An edit: the old translation off, the new words (with links) in, the mark on, the new translation state. */
    private static Edit = (Doc: IEditDoc, Bubble: IEditNode, One: AppLiveChange): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppLiveEdit.Untranslate(Bubble);
            const Words: IEditNode | null = AppLiveEdit.WordsOf(Doc, Bubble);
            if (Words === null) { return; }
            AppLinkNodes.Fill(Doc as unknown as ILinkDoc, Words as unknown as ILinkNode, One.Text);
            Bubble.setAttribute(AppEditMark.Attr, "1");
            if (One.Needed === true && One.Original.length > 0) {
                AppTranslateBubble.Mark(Doc as unknown as ITrDoc, Bubble as unknown as ITrNode, One.Original, false);
            }
            if (One.Pending === true) { Bubble.setAttribute(AppLiveRules.WaitAttr, "1"); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.Edit");
        }
    };

    /** "Deleted for everyone": no media, no translation, no mark - `[deleted]`, as the page writes a deleted row. */
    private static Revoke = (Doc: IEditDoc, Bubble: IEditNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppLiveEdit.Untranslate(Bubble);
            AppLiveEdit.Drop(Bubble, ".nomedia");
            AppLiveEdit.Drop(Bubble, ".pic");
            AppLiveEdit.Drop(Bubble, ".voice");
            Bubble.removeAttribute(AppEditMark.Attr);
            const Words: IEditNode | null = AppLiveEdit.WordsOf(Doc, Bubble);
            if (Words !== null) { (Words as IEditNode).textContent = AppEditMark.DeletedText; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.Revoke");
        }
    };

    /** The bubble's `.txt` - made (before the status line, else the clock) when a media bubble had no caption. */
    private static WordsOf = (Doc: IEditDoc, Bubble: IEditNode): IEditNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Have: IEditNode | null = Bubble.querySelector(".txt");
            if (Have !== null) { return Have; }
            const Made: IEditNode = Doc.createElement("span");
            Made.className = "txt";
            let Before: IEditNode | null = Bubble.querySelector("." + AppLiveEdit.StateClass);
            if (Before === null) { Before = Bubble.querySelector(".t"); }
            Bubble.insertBefore(Made, Before);
            return Made;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.WordsOf");
            return null;
        }
    };

    /** The translation's parts off the bubble: the hidden original, the flag, `tr` / `tron`, and the waiting mark. */
    private static Untranslate = (Bubble: IEditNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppLiveEdit.Drop(Bubble, "." + AppTranslate.OriginalClass);
            AppLiveEdit.Drop(Bubble, "." + AppTranslate.FlagClass);
            const Kept: string[] = [];
            for (const Name of nz(Bubble.className).split(" ")) {
                if (Name.length > 0 && Name !== AppTranslate.BubbleClass && Name !== AppTranslate.OpenClass) { Kept.push(Name); }
            }
            Bubble.className = Kept.join(" ");
            if (Bubble.getAttribute(AppLiveRules.WaitAttr) !== null) { Bubble.setAttribute(AppLiveRules.WaitAttr, "0"); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.Untranslate");
        }
    };

    /**
     * Take what `Selector` finds in the bubble out of it. A real element removes itself (`remove`), wherever in the
     * bubble it sits; a fake without `remove` is a child of the bubble itself.
     */
    private static Drop = (Bubble: IEditNode, Selector: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Found: IEditNode | null = Bubble.querySelector(Selector);
            if (Found === null) { return; }
            const Self: { remove?: () => void } = Found as unknown as { remove?: () => void };
            if (typeof Self.remove === "function") { Self.remove(); return; }
            Bubble.removeChild(Found as IEditNode);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppLiveEdit.Drop");
        }
    };
}
